/**
 * Hook that reconstructs the real "Steps Completed by Agent" tracker for the
 * submission status screen from RPA audit-trail data.
 *
 * It resolves the submission bot's `rpa_name` from the order's
 * `submission_type` (falling back to the audit-trail overview when that is
 * empty/unmapped), pulls that bot's detail entries, isolates the latest run,
 * and maps the granular steps onto the tracker. The audit-trail API is called
 * directly (rather than via the shared redux slices) to avoid clobbering the
 * audit-trail tab's state.
 *
 * @module useSubmissionAgentSteps
 */
import { AgentStep } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { getDetailedAuditTrial } from "../../../api/postCall/getDetailedAuditTrial";
import { DetailedEventModel } from "../../../data-model/eventsModal";
import { logError } from "../../../utils/customLogger";
import {
  buildSubmissionAgentSteps,
  getLastScreenshotPath,
  getPortalLoginUrl,
  isolateLatestRun,
} from "../utils/buildAgentStepsFromAuditTrail";
import {
  resolveSubmissionRpaForAgentSteps,
  RPA_NAME_TAG,
} from "../utils/resolveSubmissionRpaForAgentSteps";
import { SubmissionDisplayState } from "../utils/submissionDisplayState";

/** Arguments for {@link useSubmissionAgentSteps}. */
export interface UseSubmissionAgentStepsArgs {
  orderId: string;
  submissionType: string | undefined;
  displayState: SubmissionDisplayState;
}

/** Result returned by {@link useSubmissionAgentSteps}. */
export interface UseSubmissionAgentStepsResult {
  agentSteps: AgentStep[] | null;
  lastScreenshotPath: string | undefined;
  portalLoginUrl: string | undefined;
  loading: boolean;
}

/**
 * Determines whether a display state renders an agent steps tracker.
 *
 * @input displayState - The resolved submission display state.
 * @returns True when the state shows the agent tracker.
 */
const showsAgentTracker = (displayState: SubmissionDisplayState): boolean =>
  displayState === "automation_completed" ||
  displayState === "submitted_successfully" ||
  displayState === "draft_saved" ||
  displayState === "submission_failed";

/**
 * Fetches and builds the agent steps + last screenshot for a submission.
 *
 * @input args - The order id, submission type, and resolved display state.
 * @returns The agent steps, the last screenshot path, the payer-portal login
 *   URL, and a loading flag.
 */
export const useSubmissionAgentSteps = ({
  orderId,
  submissionType,
  displayState,
}: UseSubmissionAgentStepsArgs): UseSubmissionAgentStepsResult => {
  const [agentSteps, setAgentSteps] = useState<AgentStep[] | null>(null);
  const [lastScreenshotPath, setLastScreenshotPath] = useState<
    string | undefined
  >(undefined);
  const [portalLoginUrl, setPortalLoginUrl] = useState<string | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orderId || !showsAgentTracker(displayState)) {
      setAgentSteps(null);
      setLastScreenshotPath(undefined);
      setPortalLoginUrl(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadAgentSteps = async () => {
      setLoading(true);
      try {
        const rpaName = await resolveSubmissionRpaForAgentSteps(
          orderId,
          submissionType,
        );

        let latestRun: NonNullable<DetailedEventModel["entries"]> = [];
        if (rpaName) {
          const detail: DetailedEventModel = await getDetailedAuditTrial({
            order_id: orderId,
            name: rpaName,
            tag: RPA_NAME_TAG,
          });
          latestRun = isolateLatestRun(detail?.entries ?? []);
        }

        if (cancelled) {
          return;
        }

        const resolvedPortalLoginUrl = getPortalLoginUrl(latestRun);
        setAgentSteps(
          buildSubmissionAgentSteps({
            displayState,
            entries: latestRun,
            portalLoginUrl: resolvedPortalLoginUrl,
          }),
        );
        setLastScreenshotPath(getLastScreenshotPath(latestRun));
        setPortalLoginUrl(resolvedPortalLoginUrl);
      } catch (error) {
        if (cancelled) {
          return;
        }
        logError(error as Error, "Error building submission agent steps");
        // Still render the status-driven steps even if the audit fetch fails.
        setAgentSteps(
          buildSubmissionAgentSteps({
            displayState,
            entries: [],
          }),
        );
        setLastScreenshotPath(undefined);
        setPortalLoginUrl(undefined);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAgentSteps();

    return () => {
      cancelled = true;
    };
  }, [orderId, submissionType, displayState]);

  return { agentSteps, lastScreenshotPath, portalLoginUrl, loading };
};
